import { DecimalPipe } from '@angular/common';
import { HttpStatusCode } from '@angular/common/http';
import { Component, inject, input, signal } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IconButtonComponent } from '@components/buttons/icon-button/icon-button.component';
import { MenuButtonComponent } from '@components/buttons/menu-button/menu-button.component';
import {
  AccessabilityDialogComponent,
  MustBeLoginData,
} from '@components/dialogs/accessability-dialog/accessability-dialog.component';
import { ImageGeneratorComponent } from '@components/image-generator/image-generator.component';
import { LoadingSpinnerComponent } from '@components/loading/loading-spinner/loading-spinner.component';
import { JalaliDatePipe } from '@pipes/jalali-date.pipe';
import {
  Comment,
  CommentResult,
  CommentsResult,
  Episode,
  Maybe,
  PlayableEpisode,
  ProgramWithSeasons,
  Season,
} from '@shared/graphql/api.generated';
import { SnackbarService, SnackbarType } from '@shared/helper/snackbar.service';
import { AnalyticsService } from '@shared/services/analytics.service';
import { CommentService } from '@shared/services/comment.service';
import { EpisodeService } from '@shared/services/episode.service';
import { ProgramService } from '@shared/services/program.service';
import { TokenService } from '@shared/services/token.service';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-product-comment',
  imports: [
    ImageGeneratorComponent,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatTooltipModule,
    MatDividerModule,
    JalaliDatePipe,
    MatButtonModule,
    MatMenuModule,
    MenuButtonComponent,
    IconButtonComponent,
    LoadingSpinnerComponent,
    DecimalPipe,
  ],
  providers: [CommentService, ProgramService, EpisodeService, AnalyticsService],
  templateUrl: './product-comment.component.html',
  styleUrls: ['./product-comment.component.scss'],
})
export class ProductCommentComponent {
  ProgramData = input.required<ProgramWithSeasons>();
  Episode = input<Episode>();
  selectedEpisode = signal<Episode | PlayableEpisode | undefined>(undefined);
  selectedSeason = signal<Season | undefined>(undefined);
  episodesOfSelectedSeason = signal<Episode[]>([]);
  newCommentControl = new FormControl('', [Validators.required]);
  commentsData = signal<Comment[]>([]);
  submitNewCommentStatus = signal(false);
  loadingComments = signal(true);
  selectedOperationComment = signal<Comment | undefined>(undefined);
  submitLikeComment = signal(false);
  submitDeleteComment = signal(false);

  public tokenService = inject(TokenService);
  private commentService = inject(CommentService);
  private analyticsService = inject(AnalyticsService);
  private programService = inject(ProgramService);
  private episodeService = inject(EpisodeService);
  private snackbar = inject(SnackbarService);
  private dialog = inject(MatDialog);

  ngOnChanges() {
    if (this.Episode()) {
      this.getComments();
    } else {
      if (this.ProgramData().program.isSolitary) {
        this.getComments();
      } else {
        this.getResumeOfProgram();
      }
    }
  }

  private getResumeOfProgram() {
    this.analyticsService.getResumeOfProgram(this.ProgramData().program.id).subscribe({
      next: (res: any) => {
        if (res?.episode_id) {
          this.episodeService.getEpisodeById(res.episode_id).subscribe({
            next: (res) => {
              this.selectedEpisode.set(res.data!);
              this.getComments();
              this.handleSelectedSeasonOfEpisode();
            },
          });
        }
        if (this.ProgramData()) {
          this.selectedEpisode.set(this.ProgramData().solitaryEpisode!);
          this.getComments();
          this.handleSelectedSeasonOfEpisode();
        }
      },
      error: (e) => {
        if (e.status === HttpStatusCode.Unauthorized) {
          this.tokenService.refreshToken().subscribe({
            next: () => {
              this.getResumeOfProgram();
            },
          });
        }
        if (this.ProgramData()) {
          this.selectedEpisode.set(this.ProgramData().solitaryEpisode!);
          this.getComments();
          this.handleSelectedSeasonOfEpisode();
        }
      },
    });
  }

  private handleSelectedSeasonOfEpisode() {
    this.selectedSeason.set(
      this.ProgramData().seasons.filter(
        (item) => item?.id === this.selectedEpisode()?.seasonID,
      )[0]!,
    );
    this.getEpisodesOfSeason();
  }

  private getEpisodesOfSeason() {
    this.programService.getSeasonEpisodes(this.selectedSeason()!.id).subscribe({
      next: (res) => {
        this.episodesOfSelectedSeason.set(res.data);
      },
    });
  }

  private getComments() {
    this.loadingComments.set(true);
    let request: Observable<CommentsResult>;
    if (this.Episode()) {
      request = this.commentService.getComments(this.ProgramData().program.id, this.Episode()!.id);
    } else {
      if (this.ProgramData().program.isSolitary) {
        request = this.commentService.getComments(this.ProgramData().program.id);
      } else {
        request = this.commentService.getComments(
          this.ProgramData().program.id,
          this.selectedEpisode()?.id,
        );
      }
    }

    request.subscribe({
      next: (res) => {
        this.loadingComments.set(false);
        this.commentsData.set(res.data);
      },
      error: () => {
        this.loadingComments.set(false);
      },
    });
  }

  submitNewComment() {
    if (this.newCommentControl.valid) {
      let request: Observable<CommentResult>;
      if (this.tokenService.getRtk()) {
        this.submitNewCommentStatus.set(true);
        if (this.Episode()) {
          request = this.commentService.submitComment(
            this.ProgramData().program.id,
            this.newCommentControl.value!,
            this.Episode()!.id,
          );
        } else {
          if (this.ProgramData().program.isSolitary) {
            request = this.commentService.submitComment(
              this.ProgramData().program.id,
              this.newCommentControl.value!,
            );
          } else {
            request = this.commentService.submitComment(
              this.ProgramData().program.id,
              this.newCommentControl.value!,
              this.selectedEpisode()!.id,
            );
          }
        }

        request.subscribe({
          next: (res) => {
            this.submitNewCommentStatus.set(false);
            this.commentsData.update((items) => [
              {
                ...res.data!,
                timestamp: new Date().getTime() / 1000,
                profileId: this.tokenService.userInfo()!.activeProfile.id,
              },
              ...items,
            ]);
            this.newCommentControl.reset();
            this.snackbar.showSnackbar(res.status.message);
          },
          error: (e) => {
            this.submitNewCommentStatus.set(false);
            this.snackbar.showSnackbar(e.networkError.message, SnackbarType.error);
          },
        });
      } else {
        const data: MustBeLoginData = {
          message: 'برای ثبت دیدگاه، ابتدا باید وارد شوید',
        };
        this.dialog.open(AccessabilityDialogComponent, {
          data,
        });
      }
    } else {
      this.snackbar.showSnackbar(
        'برای ثبت دیدگاه، دیدگاه نمی‌تواند خالی باشد',
        SnackbarType.warning,
      );
    }
  }

  likeComment(item: Comment) {
    if (this.tokenService.getRtk()) {
      this.selectedOperationComment.set(item);
      this.submitLikeComment.set(true);
      this.commentService.likeComment(item.id, this.ProgramData().program.id).subscribe({
        next: (res) => {
          this.submitLikeComment.set(false);
          item.likedByMe = !item.likedByMe;
          item.likes += item.likedByMe ? 1 : -1;
          this.snackbar.showSnackbar(res.status.message);
        },
        error: (e) => {
          this.submitLikeComment.set(false);
          this.snackbar.showSnackbar(e.networkError.message, SnackbarType.error);
        },
      });
    } else {
      const data: MustBeLoginData = {
        message: 'برای پسندیدن دیدگاه، ابتدا باید وارد شوید',
      };
      this.dialog.open(AccessabilityDialogComponent, {
        data,
      });
    }
  }

  deleteComment(item: Comment) {
    this.selectedOperationComment.set(item);
    this.submitDeleteComment.set(true);
    this.commentService.deleteComment(item.id, this.ProgramData().program.id).subscribe({
      next: (res) => {
        this.submitDeleteComment.set(false);
        this.commentsData.update((items) => items.filter((value) => value.id !== item.id));
        this.snackbar.showSnackbar(res.status.message);
      },
      error: (e) => {
        this.submitDeleteComment.set(false);
        this.snackbar.showSnackbar(e.networkError.message, SnackbarType.error);
      },
    });
  }

  handleSeasonTitle() {
    return this.ProgramData().seasons.filter((item) => item?.id === this.Episode()?.seasonID)[0]
      ?.title;
  }

  handleSelectEpisode(episode: Episode) {
    this.selectedEpisode.set({ ...episode, seasonID: this.selectedSeason()!.id });
    this.getComments();
  }

  handleSelectSeason(event: MouseEvent, season: Maybe<Season>) {
    event.stopPropagation();
    this.selectedSeason.set(season!);
    this.getEpisodesOfSeason();
  }
}
